import type { UserDto } from "@/dto";
import { useApi } from "@/services/api";
import { sha } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import { watch } from "vue";

const CACHE_EXPIRATION_TIME = 1000 * 60 * 60; // 1 hour
const AVATAR_CACHE_EXPIRATION_TIME = 1000 * 60 * 60 * 24; // 24 hours

interface CacheEntry {
  user: UserDto;
  expiresAt: Date;
}

interface AvatarCacheEntry {
  hasProfilePicture: boolean;
  expiresAt: Date;
}

const ANONYMOUS_USER: UserDto = {
  id: "anonymous",
  email: "anonymous@probabl.ai",
  email_verified: false,
  workspaces: {},
  first_name: "Anonymous",
  last_name: "User",
  company: "Probabl",
};

const _cache: Map<string, CacheEntry> = new Map();
const _avatarCache: Map<string, AvatarCacheEntry> = new Map();

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function clearCache() {
  _cache.clear();
  _avatarCache.clear();
}

export function useUsers() {
  const api = useApi();
  const auth = useAuthStore();

  /**
   * Returns the Gravatar URL and whether a profile picture exists for the given email.
   * Results are cached for 24 hours to avoid repeated requests.
   * @param email the user's email
   * @returns object with url and hasProfilePicture flag
   */
  async function getGravatarAvatarInfo(
    email: string
  ): Promise<{ url: string; hasProfilePicture: boolean }> {
    const normalized = normalizeEmail(email);
    if (normalized.length === 0) {
      return { url: "", hasProfilePicture: false };
    }
    const key = await sha(normalized, "SHA-256");
    const url = `https://gravatar.com/avatar/${key}?d=404`;
    const now = new Date();
    const cached = _avatarCache.get(key);
    if (cached && cached.expiresAt > now) {
      return { url, hasProfilePicture: cached.hasProfilePicture };
    }
    try {
      const response = await fetch(url, { method: "HEAD" });
      const hasProfilePicture = response.ok;
      _avatarCache.set(key, {
        hasProfilePicture,
        expiresAt: new Date(Date.now() + AVATAR_CACHE_EXPIRATION_TIME),
      });
      return { url, hasProfilePicture };
    } catch {
      _avatarCache.set(key, {
        hasProfilePicture: false,
        expiresAt: new Date(Date.now() + AVATAR_CACHE_EXPIRATION_TIME),
      });
      return { url, hasProfilePicture: false };
    }
  }

  /**
   * Fetches a user by id. Results are cached for 1 hour.
   * @param id the user id
   * @returns the user DTO or undefined if not found
   */
  async function getUser(id: string) {
    if (_cache.has(id)) {
      const entry = _cache.get(id);
      if (entry && entry.expiresAt > new Date()) {
        return entry.user;
      }
    }

    if (auth.isAuthenticated) {
      const user = await api.fetchUser(id, { reportErrors: false });
      if (user) {
        _cache.set(id, { user, expiresAt: new Date(Date.now() + CACHE_EXPIRATION_TIME) });
        return user;
      }
    }

    // fallback to anonymous user
    return ANONYMOUS_USER;
  }

  watch(
    () => auth.isAuthenticated,
    () => {
      clearCache();
    }
  );

  return {
    getUser,
    getGravatarAvatarInfo,
  };
}
